// Doors live in a completely different place than windows: they're line
// items (lineType === 'door') inside `jobOrders` documents, not inside
// `cutlistJobs`. Unlike TWP windows, doors are closer to free-text estimate
// lines — `style` (Entry Door / Patio Door / Storm Door / Interior Door /
// Other) is the one field that's reliably filled in; width/height are
// optional and often blank. This module is honest about that: size
// breakdowns only ever include doors that actually have a recorded size,
// and every summary says how many doors were sized vs unsized.

// A door only counts once its job order has cleared review — 'pending_review'
// is still a quote, not a placed order.
const ORDERED_JO_STATUSES = new Set(['approved', 'scheduled', 'completed']);

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
}

function getOrderDate(jo) {
  return toDate(jo.orderDate) || toDate(jo.createdAt);
}

function roundInches(n) {
  return Math.round(n * 10000) / 10000;
}

function doorSizeLabel(width, height) {
  if (!width || !height) return null;
  return `${width}" x ${height}"`;
}

function doorColorLabel(colorOutside, colorInside) {
  const out = (colorOutside || '').trim();
  const inn = (colorInside || '').trim();
  if (out && inn && out !== inn) return `${out} out / ${inn} in`;
  return out || inn || 'Unspecified';
}

// Flattens every door line-item across approved-or-further job orders into
// one row per door, optionally restricted to a date range.
export function flattenOrderedDoors(jobOrders, { from, to } = {}) {
  const rows = [];

  jobOrders.forEach(jo => {
    if (!ORDERED_JO_STATUSES.has(jo.status)) return;

    const orderDate = getOrderDate(jo);
    if (from && (!orderDate || orderDate < from)) return;
    if (to && orderDate && orderDate > to) return;

    (jo.windows || []).forEach(w => {
      if (w.lineType !== 'door') return;
      const qty = Number(w.qty) > 0 ? Number(w.qty) : 1;
      const width = w.width ? roundInches(Number(w.width)) : null;
      const height = w.height ? roundInches(Number(w.height)) : null;
      const hasSize = Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0;

      rows.push({
        jobOrderId: jo.id,
        customerName: jo.customer?.name || jo.customer?.firstName || 'Unknown',
        orderDate: orderDate ? orderDate.toISOString().slice(0, 10) : null,
        style: w.style || w.description || 'Unspecified Door',
        width: hasSize ? width : null,
        height: hasSize ? height : null,
        hasSize,
        size: hasSize ? doorSizeLabel(width, height) : null,
        color: doorColorLabel(w.colorOutside, w.colorInside),
        qty,
      });
    });
  });

  return rows;
}

function aggregateRows(rows, { key, initExtra, updateExtra }) {
  const map = new Map();

  rows.forEach(r => {
    const k = key(r);
    if (!map.has(k)) {
      map.set(k, { key: k, timesOrdered: 0, totalQty: 0, jobIds: new Set(), ...(initExtra ? initExtra(r) : {}) });
    }
    const entry = map.get(k);
    entry.timesOrdered += 1;
    entry.totalQty += r.qty;
    entry.jobIds.add(r.jobOrderId);
    if (updateExtra) updateExtra(entry, r);
  });

  const totalQtyAll = rows.reduce((sum, r) => sum + r.qty, 0);

  return Array.from(map.values())
    .map(e => ({ ...e, jobCount: e.jobIds.size, pctOfTotal: totalQtyAll > 0 ? (e.totalQty / totalQtyAll) * 100 : 0 }))
    .sort((a, b) => b.totalQty - a.totalQty);
}

// Ranks (door type, COLOR) combinations by units ordered. Color is part of
// the grouping key, not a tally — a White Entry Door and a Clay Entry Door
// are different products, so they're always separate rows.
export function summarizeDoorsByType(rows) {
  return aggregateRows(rows, {
    key: r => `${r.style}|${r.color}`,
    initExtra: r => ({ style: r.style, color: r.color }),
  });
}

// Ranks EXACT (style, width x height, COLOR) combinations — only doors that
// actually have a recorded size are included here.
export function summarizeDoorsBySize(rows) {
  const sizedRows = rows.filter(r => r.hasSize);
  return aggregateRows(sizedRows, {
    key: r => `${r.style}|${r.width}|${r.height}|${r.color}`,
    initExtra: r => ({ style: r.style, size: r.size, width: r.width, height: r.height, color: r.color }),
  });
}

export function summarizeDoorsByMonth(rows) {
  const map = new Map();
  rows.forEach(r => {
    if (!r.orderDate) return;
    const d = new Date(r.orderDate);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!map.has(key)) {
      map.set(key, { key, label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), totalQty: 0, timesOrdered: 0 });
    }
    const entry = map.get(key);
    entry.totalQty += r.qty;
    entry.timesOrdered += 1;
  });
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
}

// How many door line-items actually have a usable width/height, so the UI
// can be upfront about size-report coverage instead of implying every door
// is measured.
export function doorSizeCoverage(rows) {
  const sized = rows.filter(r => r.hasSize).length;
  return { sized, unsized: rows.length - sized, total: rows.length };
}
